import constants from '../../../plugins/constants';

const { AUTH, API_URLS } = constants;

export const AUTH_BRAND_PANEL_CAROUSEL_INTERVAL_MS = 8000;
export const LOGIN_SCREENS_API_TIMEOUT_MS = 4000;
export const LOGIN_SCREENS_IFRAME_TIMEOUT_MS = 3000;
export const LOGIN_SCREENS_QUOTE_INTERVAL_MS = 10000;
/**
 * Login visuals may run inline animation scripts and open CTA links in a new tab.
 * Omit allow-same-origin so the framed page cannot access the parent.
 */
export const AUTH_BRAND_PANEL_IFRAME_SANDBOX =
  'allow-scripts allow-popups allow-popups-to-escape-sandbox';

const LOGIN_SCREEN_IFRAME_ALLOWED_AWS_HOST =
  /^[a-z0-9.-]+\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;
const LOGIN_SCREEN_IFRAME_ALLOWED_APP_HOST_SUFFIXES = ['.consarc.ai', '.consark.ai'];
const LOGIN_SCREEN_IFRAME_AWS_PATH_FRAGMENT = '/login-screens/';
const LOGIN_SCREEN_IFRAME_APP_PATH_PREFIX = '/img/auth/panel/';
const LOGIN_SCREEN_FALLBACK_IFRAME_PATH = '/img/auth/login-visual.html';

/** Shown when /v2/auth/login-screens returns no slides (public/img/auth/login-visual.html). */
export const LOGIN_SCREEN_DEFAULT_SLIDE = {
  id: 'login-visual-default',
  iframeSrc: LOGIN_SCREEN_FALLBACK_IFRAME_PATH,
  heading: 'Consark is now Consarc.',
  subhead: 'Noa AI Agents Execute the Close in Parallel.',
  cta: 'Read the note from our CEO.',
  ctaHref: 'https://www.consarc.ai/blogs/consarc-the-future-of-close',
};

let loginScreensInflight = null;

function normalizeHostname(hostname) {
  return String(hostname || '')
    .toLowerCase()
    .replace(/\.$/, '');
}

function hostMatchesSuffix(hostname, suffix) {
  const host = normalizeHostname(hostname);
  const normalizedSuffix = suffix.startsWith('.') ? suffix.slice(1) : suffix;
  return host === normalizedSuffix || host.endsWith(`.${normalizedSuffix}`);
}

function isAllowedLoginScreenIframeSrc(value) {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes('@')) return false;
  try {
    const base =
      typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
    const url = new URL(trimmed, base);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    if (url.username || url.password) return false;

    const host = normalizeHostname(url.hostname);
    if (LOGIN_SCREEN_IFRAME_ALLOWED_AWS_HOST.test(host)) {
      return url.protocol === 'https:' && url.pathname.includes(LOGIN_SCREEN_IFRAME_AWS_PATH_FRAGMENT);
    }

    const onAppHost = LOGIN_SCREEN_IFRAME_ALLOWED_APP_HOST_SUFFIXES.some((suffix) =>
      hostMatchesSuffix(host, suffix),
    );
    const onSameOrigin =
      typeof window !== 'undefined' && url.origin === window.location.origin;
    if (onAppHost || onSameOrigin) {
      if (url.pathname === LOGIN_SCREEN_FALLBACK_IFRAME_PATH) return true;
      return url.pathname.startsWith(LOGIN_SCREEN_IFRAME_APP_PATH_PREFIX);
    }
    return false;
  } catch {
    return false;
  }
}

function pickAllowedIframeSrc(raw) {
  if (!isAllowedLoginScreenIframeSrc(raw)) return '';
  return raw.trim();
}

function isHttpUrl(value) {
  if (!value || typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

function resolveCtaHref(href) {
  return isHttpUrl(href) ? href.trim() : '';
}

function withDefaultSlides(slides) {
  return slides.length > 0 ? slides : [LOGIN_SCREEN_DEFAULT_SLIDE];
}

function emptyLoginScreensPayload() {
  return normalizeLoginScreensPayload({});
}

function normalizeRemoteSlides(payload) {
  const slides = Array.isArray(payload?.slides) ? payload.slides : [];
  return slides
    .map((slide) => {
      if (!slide) return null;
      const iframeSrc = pickAllowedIframeSrc(slide.iframeSrc);
      if (!iframeSrc) return null;
      return {
        id: slide.id,
        iframeSrc,
        heading: slide.heading,
        subhead: slide.subhead,
        cta: slide.cta,
        ctaHref: resolveCtaHref(slide.ctaHref),
      };
    })
    .filter(Boolean);
}

function normalizeQuotes(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((quote) => typeof quote === 'string' && quote.trim())
    .map((quote) => quote.replace(/\s+/g, ' ').trim());
}

function normalizeLoginScreensPayload(payload) {
  return {
    slides: withDefaultSlides(normalizeRemoteSlides(payload)),
    termsHref: isHttpUrl(payload?.termsHref) ? payload.termsHref : '',
    privacyHref: isHttpUrl(payload?.privacyHref) ? payload.privacyHref : '',
    loaderText:
      typeof payload?.loaderText === 'string' && payload.loaderText.trim()
        ? payload.loaderText.trim()
        : AUTH.LOADER_TEXT,
    quotes: normalizeQuotes(payload?.quotes),
  };
}

/**
 * Module-level in-flight/cached fetch so Login ↔ ForgotPassword does not refetch.
 * Failures fall back to LOGIN_SCREEN_DEFAULT_SLIDE; legal links and quotes come from the API when present.
 */
export function fetchLoginBrandSlides(http) {
  if (loginScreensInflight) return loginScreensInflight;
  loginScreensInflight = (async () => {
    try {
      const response = await http.get(API_URLS.AUTH_LOGIN_SCREENS, {
        skipAuthRefresh: true,
        timeout: LOGIN_SCREENS_API_TIMEOUT_MS,
      });
      const raw = response?.data?.data;
      const payload = Array.isArray(raw) ? raw[0] : raw;
      return normalizeLoginScreensPayload(payload);
    } catch {
      return emptyLoginScreensPayload();
    }
  })();
  return loginScreensInflight;
}

export function waitWithTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}
